import { computed, ref, watch, type MaybeRefOrGetter, toValue, type Ref } from 'vue';
import { orderBy } from 'lodash';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { textMessageService } from '@/services/textMessage';
import type { TextMessage } from '@/models/textMessage';

const HISTORY_LIMIT = 50;

export const useSmsConversationHistory = (
  phoneNumber?: MaybeRefOrGetter<string | null | undefined>,
  options?: { rideId?: MaybeRefOrGetter<string | number | null | undefined> },
) => {
  const { showErrorPopupDialog } = useSwalDialog();

  const isLoadingHistory = ref(false);
  const messages: Ref<TextMessage[]> = ref([]);

  const canLoadHistory = computed(() => {
    if (options?.rideId) {
      return Boolean(toValue(options.rideId));
    }
    return util.isPhoneNumberValid(toValue(phoneNumber) || '');
  });

  const loadMessageHistory = async () => {
    if (!canLoadHistory.value) {
      messages.value = [];
      return;
    }

    const phone = toValue(phoneNumber) || '';
    const rideId = options?.rideId ? toValue(options.rideId) : null;

    isLoadingHistory.value = true;
    try {
      const response = await textMessageService.fetchTextMessages({
        type: rideId ? 'rideId' : 'phoneNumber',
        query: rideId ? String(rideId) : phone,
        pagination: { limit: HISTORY_LIMIT, offset: 0 },
      });
      messages.value = orderBy(response.rows ?? [], ['createdAt'], ['asc']);
    } catch (error) {
      messages.value = [];
      showErrorPopupDialog({ text: util.getGraphqlError(error, false) });
    }
    isLoadingHistory.value = false;
  };

  let historyLoadTimeout: ReturnType<typeof setTimeout> | undefined;
  watch(
    () => [toValue(phoneNumber), options?.rideId ? toValue(options.rideId) : null] as const,
    () => {
      clearTimeout(historyLoadTimeout);
      historyLoadTimeout = setTimeout(loadMessageHistory, 300);
    },
    { immediate: true },
  );

  return {
    messages,
    isLoadingHistory,
    isPhoneNumberValid: canLoadHistory,
    loadMessageHistory,
  };
};
